import proj4 from 'proj4';
import { BufferGeometry, Float32BufferAttribute, Points, Vector3 } from 'three';

import {
  BinaryBodyReference,
  CesiumPnts,
  getFeatureElementSize,
  PntsFeatureTable,
} from '../../../../../loader/cesium/content/pnts/types';
import { PointBuilderOptions } from '../factory';
import { BufferAttributeAdapter } from './buffer-attribute-adapter';

export const DefaultPositionBufferAttribute: BufferAttributeAdapter = (
  feature: keyof PntsFeatureTable,
  pntsData: CesiumPnts,
) => {
  const buffer = pntsData.features.featureBody;
  const offset = (pntsData.features.featureTable[feature] as BinaryBodyReference).byteOffset;
  const pointsCount = pntsData.features.featureTable.POINTS_LENGTH;
  return new Float32BufferAttribute(
    new Float32Array(buffer, offset, getFeatureElementSize(feature) * pointsCount),
    getFeatureElementSize(feature),
  );
};

/**
 *  Transforms position attributes to the projects projection
 *  TODO: Migrate to the backend process
 */
export const TransformPositionBufferAttribute: BufferAttributeAdapter = (
  feature: keyof PntsFeatureTable,
  pntsData: CesiumPnts,
  options: PointBuilderOptions,
) => {
  const buffer = pntsData.features.featureBody;
  const offset = (pntsData.features.featureTable[feature] as BinaryBodyReference).byteOffset;
  const pointsCount = pntsData.features.featureTable.POINTS_LENGTH;

  const originalPointPositions = new Float32Array(
    buffer,
    offset,
    getFeatureElementSize(feature) * pointsCount,
  );

  const rtcCenter = pntsData.features.featureTable.RTC_CENTER;
  const rtcCenterSweref99 = proj4('wgs84m', options.transformPointsToProjection!, rtcCenter);
  for (let i = 0; i < pointsCount; i++) {
    const sweref99Position = proj4('wgs84m', options.transformPointsToProjection!, [
      originalPointPositions[i * 3 + 0] + rtcCenter[0],
      originalPointPositions[i * 3 + 1] + rtcCenter[1],
      originalPointPositions[i * 3 + 2] + rtcCenter[2],
    ]);
    originalPointPositions[i * 3 + 0] = sweref99Position[0] - rtcCenterSweref99[0];
    originalPointPositions[i * 3 + 1] = sweref99Position[1] - rtcCenterSweref99[1];
    originalPointPositions[i * 3 + 2] = sweref99Position[2] - rtcCenterSweref99[2];
  }

  return new Float32BufferAttribute(originalPointPositions, getFeatureElementSize(feature));
};

/**
 *  Extract the point position at index position
 */
export const getAttributePositionAtIndex = (
  points: Points,
  index: number,
  offset: Vector3 = new Vector3(),
): Vector3 => {
  const pointGeometry: BufferGeometry = points.geometry;
  const pointPositionAttribute = pointGeometry.attributes.position;
  return new Vector3(
    pointPositionAttribute.getX(index) + offset.x,
    pointPositionAttribute.getY(index) + offset.y,
    pointPositionAttribute.getZ(index) + offset.z,
  );
};
